import React, { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Stack, Text } from '@mantine/core';
import Button from '../../../components/Button';
import { AppState, isModuleSubscriptionProduct } from '../../../types';
import { centsToDollarsNumber, getLocalizedPricing } from '../../../lib/util';
import { useLocale } from '../../../contexts/LocaleContext';
import { getShortSubscriptionName } from '../util/helpers';

interface ConvertTrialConfirmContentProps {
  priceInCents: number;
  renewalMonths: number;
  onConfirm: () => void;
  onClose: () => void;
  loading: boolean;
}

export function ConvertTrialConfirmContent({
  priceInCents,
  renewalMonths,
  onConfirm,
  onClose,
  loading,
}: ConvertTrialConfirmContentProps) {
  const { t } = useTranslation();
  const { currencyFormatter, currency } = useLocale();
  const products = useSelector((state: AppState) => state.config.products);

  // The free trial plan is configured to not have an activation fee because
  // we never charge an activation fee for the free trial plan. For marketing
  // purposes, we want to show them what they typically would have paid for the
  // activation fee on the non-free trial plan.
  const effectiveActivationFeeInCents = useMemo(() => {
    const product = products
      .filter(isModuleSubscriptionProduct)
      .find((p) => !p.freeTrial && p.activationFeePricingInCents && p.renewalMonths === renewalMonths);
    if (!product?.activationFeePricingInCents) return 0;
    return getLocalizedPricing(product.activationFeePricingInCents, currency);
  }, [products, currency, renewalMonths]);

  const planName = getShortSubscriptionName(t, renewalMonths);
  const priceFormatted = currencyFormatter.format(centsToDollarsNumber(priceInCents));
  const originalPrice =
    effectiveActivationFeeInCents > 0
      ? currencyFormatter.format(centsToDollarsNumber(priceInCents + effectiveActivationFeeInCents))
      : undefined;

  const renewalPeriod = renewalMonths === 1 ? t('month') : t('months_months', { months: renewalMonths });

  return (
    <Stack>
      <Text fz={24} fw={600}>
        {t('confirm_membership_change')}
      </Text>
      <Text fz={16} fw={600}>
        <Trans
          i18nKey="your_trial_will_end_and_start_membership"
          values={{
            plan: planName,
            price: priceFormatted,
            originalPrice: originalPrice ?? '',
            renewalPrice: priceFormatted,
            renewalPeriod,
          }}
          components={{
            s: <Text span td="line-through" />,
          }}
        />
      </Text>
      <Button onClick={onConfirm} loading={loading}>
        {t('confirm')}
      </Button>
      <Button onClick={onClose} tertiary>
        {t('nevermind')}
      </Button>
    </Stack>
  );
}
